/**
 * buildFullQuery — multi-object SQL generator for FrankenQL Builder V2.
 *
 * Pipeline reference: .claude/skills/frankenql-pipeline.md  (Layer 1)
 * Documents how alias prefixes (sf./hs.) set here flow through alias_resolver →
 * antlr_adapter FormatDuckDB → duckdb_tenant ATTACH, and what breaks if they're missing.
 *
 * Pure function, Node-testable. Takes full builder state and returns a
 * complete SELECT statement with LEFT JOINs for lookups and LIST aggregate
 * subqueries for child relationships.
 *
 * @param {object} params
 * @param {string}   params.baseObject         e.g. 'Contact'
 * @param {string|null} params.baseAlias       data source alias e.g. 'sf'. If set, emits 'FROM sf.Contact c'
 * @param {string[]} params.baseFields          e.g. ['Id', 'LastName', 'Email']
 * @param {Object}   params.lookupSelections    { [lookupField]: { targetObject, targetField, sourceAlias, selectedFields[] } }
 * @param {Object}   params.childSelections     { [relName]: { childObject, joinField, childDsAlias, selectedFields[] } }
 * @param {Array}    params.manualJoins         [{ leftField, rightObject, rightField }]
 * @param {number}   params.limit
 * @returns {{ sql: string, mode: 'replace' }}
 */
export function buildFullQuery({
  baseObject,
  baseAlias: dsAlias = null,
  baseFields = [],
  lookupSelections = {},
  childSelections = {},
  manualJoins = [],
  limit = 1000,
}) {
  const clampedLimit = Math.min(Math.max(1, parseInt(limit) || 1000), 10000);
  const takenAliases = new Set();
  const baseAlias = objectToAlias(baseObject, takenAliases);

  const selectCols = [];
  const joinClauses = [];

  // Base fields
  for (const f of baseFields) {
    selectCols.push(`${baseAlias}.${f}`);
  }

  // Lookup LEFT JOINs
  for (const [lookupField, { targetObject, targetField, sourceAlias: lookupDsAlias, selectedFields }] of Object.entries(lookupSelections)) {
    if (!selectedFields || selectedFields.length === 0) continue;
    const alias = objectToAlias(targetObject, takenAliases);
    const joinTarget = targetField || 'Id';
    for (const f of selectedFields) {
      selectCols.push(`${alias}.${f}`);
    }
    const qualifiedTarget = lookupDsAlias ? `${lookupDsAlias}.${targetObject}` : targetObject;
    joinClauses.push(
      `LEFT JOIN ${qualifiedTarget} ${alias} ON ${baseAlias}.${lookupField} = ${alias}.${joinTarget}`
    );
  }

  // Child LIST aggregate subquery JOINs
  for (const [relName, { childObject, joinField, selectedFields, childDsAlias }] of Object.entries(childSelections)) {
    if (!selectedFields || selectedFields.length === 0) continue;
    const childAlias = objectToAlias(relName.toLowerCase(), takenAliases);
    const listCols = selectedFields
      .map(f => `LIST(${f}) AS ${relName}_${f}`)
      .join(',\n    ');
    for (const f of selectedFields) {
      selectCols.push(`${childAlias}.${relName}_${f}`);
    }
    const qualifiedChild = childDsAlias ? `${childDsAlias}.${childObject}` : childObject;
    joinClauses.push(
      `LEFT JOIN (\n  SELECT ${joinField},\n    ${listCols}\n  FROM ${qualifiedChild}\n  GROUP BY ${joinField}\n) ${childAlias} ON ${childAlias}.${joinField} = ${baseAlias}.Id`
    );
  }

  // Manual joins (user-defined relationships)
  for (const { leftField, rightObject, rightField } of manualJoins) {
    const alias = objectToAlias(rightObject, takenAliases);
    joinClauses.push(
      `LEFT JOIN ${rightObject} ${alias} ON ${baseAlias}.${leftField} = ${alias}.${rightField}`
    );
  }

  const selectList = selectCols.length > 0 ? selectCols.join(',\n       ') : `${baseAlias}.*`;
  const qualifiedBase = dsAlias ? `${dsAlias}.${baseObject}` : baseObject;
  const fromClause = `FROM ${qualifiedBase} ${baseAlias}`;
  const joinBlock = joinClauses.length > 0 ? '\n' + joinClauses.join('\n') : '';

  const sql = `SELECT ${selectList}\n${fromClause}${joinBlock}\nLIMIT ${clampedLimit}`;
  return { sql, mode: 'replace' };
}

/**
 * Generate a short alias for an object name, collision-safe.
 * Takes the first letter lowercase; if taken, grows by one char, etc.
 *
 * @param {string} name
 * @param {Set<string>} taken
 * @returns {string}
 */
export function objectToAlias(name, taken) {
  if (!name) return 'x';
  const base = name.toLowerCase().replace(/[^a-z0-9]/g, '');
  let alias = base[0] || 'x';
  let i = 1;
  while (taken.has(alias)) {
    if (i < base.length) {
      alias = base.slice(0, i + 1);
    } else {
      alias = base + i;
    }
    i++;
  }
  taken.add(alias);
  return alias;
}
